import React, { useState } from 'react';
import { ManualCustomListSelectorCode, selectorConfigByType } from './ListGroups';
import {
  InternalApiListSelectorGroup,
  InternalApiListSelectorOption,
  InternalApiUpdateListSelectorGroupCommand,
  UpdateSelectorGroupByCodeRequest,
} from '../../../api-internal';
import { GroupValidationResult, SelectorGroup } from './SelectorOption';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { SalvButton, SalvSearchField, Size } from '@salvapp/ui-components';
import { useSelectorNavigation } from './useSelectorNavigation';
import { useSelectorOptions } from './useSelectorOptions';
import { useSelectorGroups } from './useSelectorGroups';
import { useGroupSummary } from './useGroupSummary';
import { useSelectorOptionFilter } from './useSelectorOptionFilter';
import { useFilter } from '@react-aria/i18n';
import SelectorOptionColumn from './SelectorOptionColumn';
import './Selector.scss';
import { clsx } from 'clsx';
import Has from '../../../auth/Has';
import { CAN_ADD_SCREENING_SELECTOR_GROUP } from '../../../auth/permissions';

export type SelectorOptionType = Pick<InternalApiListSelectorOption, 'value' | 'descriptions'> & {
  children: SelectorOptionType[];
  title: string;
  isSelected?: boolean;
  isSubscribed?: boolean;
};

export type SelectorOptionMap = Map<string, SelectorOptionType>;

interface Props {
  type: ManualCustomListSelectorCode;
  apiSelectorOptions: InternalApiListSelectorOption[];
  apiScreeningSelectorGroups: InternalApiListSelectorGroup[];
}

type ConditionalProps =
  | {
      onSelectGroupCode?: undefined;
      selectedGroupCodes?: undefined;
      createGroup: (req: InternalApiUpdateListSelectorGroupCommand) => Promise<InternalApiListSelectorGroup>;
      updateGroup: (
        req: Omit<UpdateSelectorGroupByCodeRequest, 'listId'>
      ) => Promise<InternalApiListSelectorGroup | undefined>;
      deleteGroup: (groupCode: string) => void;
    }
  | {
      createGroup?: undefined;
      updateGroup?: undefined;
      deleteGroup?: undefined;
      onSelectGroupCode: (groupCode: string) => (isToggled: boolean) => void;
      selectedGroupCodes: string[];
    };

const Selector = ({
  type,
  apiSelectorOptions,
  apiScreeningSelectorGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  onSelectGroupCode,
  selectedGroupCodes,
}: ConditionalProps & Props) => {
  const selectorConfig = selectorConfigByType[type];
  const { rootOptionValue } = selectorConfig;
  const optionDepth = selectorConfig.selectorOptionHeaders.length;
  const isGroupSelectionMode = !!onSelectGroupCode;

  const { groups, firstGroup, editedGroup, setEditedGroup, isGroupEdited } = useSelectorGroups({
    apiScreeningSelectorGroups,
    type,
    apiSelectorOptions,
  });

  const { selectorOptionMap, setSelectorOptionMap, applyGroupToSelectorOptions } = useSelectorOptions({
    apiSelectorOptions,
    type,
    firstGroup,
  });

  const { contains } = useFilter({ sensitivity: 'base' });
  const { filters, setFilters, clearFilters, clearChildrenFilters } = useSelectorOptionFilter({
    optionDepth,
  });

  const { navigationIndex, navigateFromGroup, navigateFromOption, cancelChildrenNavigation } = useSelectorNavigation({
    type,
    initialFirstGroup: firstGroup,
    initialFirstSelectorOption: selectorOptionMap.get(rootOptionValue)!.children[0],
    applyGroupToSelectorOptions,
    optionDepth,
    clearChildrenFilters,
  });

  const { groupSummary, getAllGroupSummary } = useGroupSummary({
    selectorOptionMap,
  });

  const setGroupFilter = (value: string) => {
    if (value) {
      cancelChildrenNavigation(0);
    } else {
      // Restore navigation
      navigateFromGroup(firstGroup);
    }
    setFilters((prev) => {
      prev[0] = value;
      return [...prev];
    });
  };

  if (isGroupSelectionMode && groups.length === 0) {
    return <div className='title-s m-l-24 m-t-16'>No groups available</div>;
  }

  const cancelEdit = () => {
    navigateFromGroup(editedGroup?.code ? editedGroup : firstGroup);
    setEditedGroup(undefined);
    setValidationResult(undefined);
  };

  const [validationResult, setValidationResult] = useState<GroupValidationResult>();

  return (
    <>
      <div className={'selector-grid'}>
        <div className={'selector-option-column'}>
          <div className={'selector-option-filter'}>
            <SalvSearchField
              label={'List Groups'}
              size={Size.SMALL}
              placeholder={'All'}
              value={filters[0]}
              onChange={setGroupFilter}
              onSubmit={setGroupFilter}
            />
          </div>
          <div className={'selector-option-list'}>
            {groups
              .filter((group) => contains(group.name, filters[0]))
              .map((group) => (
                <SelectorGroup
                  key={group.code}
                  navigate={() => {
                    if (group.code !== navigationIndex[0]) {
                      navigateFromGroup(group);
                    }
                  }}
                  isNavigated={navigationIndex[0] === group.code}
                  name={editedGroup?.code === group.code ? editedGroup.name : group.name}
                  setName={(name) => {
                    setValidationResult(undefined);
                    setEditedGroup((prev) => {
                      return {
                        ...prev!,
                        name,
                      };
                    });
                  }}
                  editedGroup={editedGroup}
                  group={group}
                  setEdit={() => {
                    setEditedGroup(group);
                    navigateFromGroup(group);
                  }}
                  deleteGroup={deleteGroup}
                  onSelectGroupCode={onSelectGroupCode}
                  selectedGroupCodes={selectedGroupCodes ?? []}
                  validationResult={validationResult}
                />
              ))}
            {!isGroupSelectionMode && !isGroupEdited && (
              <Has allPermissions={[CAN_ADD_SCREENING_SELECTOR_GROUP]}>
                <div style={{ padding: '4px' }}>
                  <SalvButton
                    label={'New DJ Sanction Group'}
                    icon={faPlus}
                    variant={'muted'}
                    className={'new-group-button'}
                    onClick={() => {
                      const newGroup: InternalApiListSelectorGroup = {
                        name: '',
                        code: '',
                        selectorCode: type,
                        enabledSelectorOptions: [],
                        subscribedSelectorOptions: [],
                      };
                      navigateFromGroup(newGroup);
                      clearFilters();
                      setEditedGroup(newGroup);
                    }}
                    size={Size.SMALL}
                  />
                </div>
              </Has>
            )}
          </div>
        </div>
        {[...Array(optionDepth)].map((_, i) => (
          <SelectorOptionColumn
            key={i}
            index={i}
            type={type}
            selectorOptionMap={selectorOptionMap}
            setSelectorOptionMap={(map) => {
              setSelectorOptionMap(map);
              setValidationResult(undefined);
            }}
            navigateFromOption={navigateFromOption}
            cancelChildrenNavigation={cancelChildrenNavigation}
            navigationIndex={navigationIndex}
            filters={filters}
            setFilters={setFilters}
            isGroupEdited={isGroupEdited}
          />
        ))}
      </div>
      <div className={clsx('group-summary', validationResult?.noOptionsAreSelected && 'group-summary-invalid')}>
        <div>
          {!groupSummary.enabledSelectorOptions.length
            ? 'Nothing selected yet'
            : `Selected ${groupSummary.enabledSelectorOptions.length} options`}
        </div>
        {editedGroup && (
          <div className={'group-summary-actions'}>
            <SalvButton label={'Cancel'} variant={'muted'} onClick={cancelEdit} />
            <SalvButton
              label={'Save'}
              disabled={validationResult?.nameIsBlank || validationResult?.noOptionsAreSelected}
              onClick={async () => {
                // DJ PERSON/ENTITY Reference Code workaround
                const {
                  enabledSelectorOptions: allEnabledSelectorOptions,
                  subscribedSelectorOptions: allSubscribedSelectorOptions,
                } = getAllGroupSummary(selectorOptionMap, apiSelectorOptions, type);

                const nameIsBlank = !editedGroup.name;
                const noOptionsAreSelected = allEnabledSelectorOptions.length === 0;
                setValidationResult({ nameIsBlank, noOptionsAreSelected });
                if (nameIsBlank || noOptionsAreSelected) {
                  return;
                }

                const groupCommand: InternalApiUpdateListSelectorGroupCommand = {
                  name: editedGroup.name,
                  selectorCode: type,
                  enabledSelectorOptions: allEnabledSelectorOptions,
                  subscribedSelectorOptions: allSubscribedSelectorOptions,
                };

                let updatedGroup: InternalApiListSelectorGroup | undefined;
                if (!editedGroup?.code) {
                  updatedGroup = await createGroup?.(groupCommand);
                } else {
                  updatedGroup = await updateGroup?.({
                    groupCode: editedGroup.code,
                    internalApiUpdateListSelectorGroupCommand: groupCommand,
                  });
                }

                if (updatedGroup) {
                  navigateFromGroup({
                    ...updatedGroup,
                    // DJ PERSON/ENTITY Reference Code workaround
                    enabledSelectorOptions: groupSummary.enabledSelectorOptions,
                    subscribedSelectorOptions: groupSummary.subscribedSelectorOptions,
                  });
                  setEditedGroup(undefined);
                } else {
                  cancelEdit();
                }
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Selector;
